import { Box, Portal } from '@mantine/core';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { ConfirmationPopup, ConfirmationPopupVariant } from './ConfirmationPopup';

const AUTO_DISMISS_MS = 1500;

type GlobalConfirmationContextType = {
  showConfirmation: (label: string, variant?: ConfirmationPopupVariant) => void;
};

const GlobalConfirmationContext = createContext<GlobalConfirmationContextType>({
  showConfirmation: () => {},
});

type ConfirmationState = {
  label: string;
  variant: ConfirmationPopupVariant;
};

// Renders the centred ConfirmationPopup above the router so it survives the
// navigation that follows most success actions (create family, add member,
// disband, etc.). Auto-dismisses after a short delay.
export function GlobalConfirmationProvider({ children }: { children: ReactNode }) {
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(null);

  useEffect(() => {
    if (!confirmation) return;
    const timeout = setTimeout(() => setConfirmation(null), AUTO_DISMISS_MS);
    return () => clearTimeout(timeout);
  }, [confirmation]);

  const showConfirmation = (label: string, variant: ConfirmationPopupVariant = 'success') => {
    setConfirmation({ label, variant });
  };

  return (
    <GlobalConfirmationContext value={{ showConfirmation }}>
      {children}
      {confirmation && (
        <Portal>
          <Box
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            <ConfirmationPopup label={confirmation.label} variant={confirmation.variant} />
          </Box>
        </Portal>
      )}
    </GlobalConfirmationContext>
  );
}

export function useGlobalConfirmation() {
  return useContext(GlobalConfirmationContext);
}
